/**
 * localStorage for the Analyzer's in-progress OnboardingDraft.
 *
 * One draft at a time — the user resumes whatever they last started. The
 * draft is finalized into a StructuredWorldview on completion (see
 * structuredWorldviewStorage.ts) and the draft is cleared.
 *
 * Reads and writes are tolerant of older shapes: a missing field falls back
 * to defaults rather than throwing, so a v1 user upgrading to a v2 schema
 * doesn't lose their in-progress session.
 */

import type { OnboardingDraft, OnboardingDraftStage1 } from '@arena/shared';

const DRAFT_KEY = 'arena.onboardingDraft.v1';

function emptyStage1(): OnboardingDraftStage1 {
  return { questionIds: [], answers: {}, swapsUsed: 0 };
}

export function loadDraft(): OnboardingDraft | null {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<OnboardingDraft> | null;
    if (!parsed || typeof parsed !== 'object') return null;
    if (typeof parsed.id !== 'string') return null;
    return {
      id: parsed.id,
      stage: parsed.stage ?? 0,
      name: parsed.name,
      stage1: parsed.stage1 ?? emptyStage1(),
      stage2: parsed.stage2,
      stage3: parsed.stage3,
      stage4: parsed.stage4,
      createdAt: parsed.createdAt ?? new Date().toISOString(),
      updatedAt: parsed.updatedAt ?? new Date().toISOString(),
    };
  } catch {
    return null;
  }
}

export function saveDraft(draft: OnboardingDraft): void {
  try {
    const stamped: OnboardingDraft = {
      ...draft,
      updatedAt: new Date().toISOString(),
    };
    localStorage.setItem(DRAFT_KEY, JSON.stringify(stamped));
  } catch {
    // quota exceeded or storage unavailable — swallow rather than crash UI
  }
}

export function clearDraft(): void {
  try {
    localStorage.removeItem(DRAFT_KEY);
  } catch {
    // ignore
  }
}

/** Create a fresh draft. Pass the sampled question ids for Stage 1. */
export function createDraft(questionIds: string[]): OnboardingDraft {
  const now = new Date().toISOString();
  return {
    id: `draft_${Date.now()}`,
    stage: 0,
    stage1: {
      questionIds,
      answers: {},
      swapsUsed: 0,
    },
    createdAt: now,
    updatedAt: now,
  };
}
